// src/hooks/useAuth.js
// --- AUTH HOOK ----------------------------------------------------------------
import { useState, useEffect, useRef, createContext, useContext } from 'react';
import { auth, db } from '../lib/supabase';
import { localSession } from '../lib/offlineDB';
import { anzahl as wartendeAnzahl } from '../lib/warteschlange';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null); // { role: 'monteur'|'buero'|'admin', name, email }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Das zuletzt ECHT geladene Profil. Scheitert das Nachladen (schlechtes
  // Netz, stuendliche Token-Erneuerung), bleibt es gueltig. Vorher fiel die
  // App dann auf "Monteur" zurueck: Das Buero verlor Planung und Protokolle,
  // eine offene Planung wurde mitten im Tippen geschlossen.
  const echtesProfil = useRef(null);

  // Beim Start: Prüfe ob Session vorhanden (auch offline)
  useEffect(() => {
    let ignore = false;

    // Hilfsfunktion: Promise mit Timeout wrappen, damit App nie ewig hängt
    const withTimeout = (p, ms, label) =>
      Promise.race([
        p,
        new Promise((_, rej) => setTimeout(() => rej(new Error(`Timeout: ${label}`)), ms)),
      ]);

    async function initAuth() {
      try {
        // Zuerst lokalen Cache prüfen (für Offline-Start)
        const cached = await withTimeout(localSession.get(), 2000, 'localSession.get').catch(() => null);
        if (cached && !ignore) {
          setUser(cached.user);
          setProfile(cached.profile);
          if (cached.profile?.id) echtesProfil.current = cached.profile;
          // Das Geraet kennt den Nutzer schon: SOFORT starten, die Anmeldung
          // wird im Hintergrund aufgefrischt. Vorher stand bei schwachem Netz
          // bis zu 15 s "ETB lädt..." (Testlauf 13.09.2026).
          if (cached.user && cached.profile) setLoading(false);
        }

        // Dann aktuelle Session von Supabase holen (mit Timeout-Schutz)
        const sessionResult = await withTimeout(auth.getSession(), 12000, 'auth.getSession').catch(() => ({ data: { session: null } }));
        const { data: { session } } = sessionResult;
        if (session && !ignore) {
          const profResult = await withTimeout(db.getUserProfile(session.user.id), 12000, 'getUserProfile').catch(() => ({ data: null }));
          const bekannt = echtesProfil.current?.id === session.user.id ? echtesProfil.current : null;
          const profileData = profResult.data || bekannt || { role: 'monteur', name: session.user.email };
          if (profResult.data) echtesProfil.current = profResult.data;
          setUser(session.user);
          setProfile(profileData);
          // Lokal cachen für Offline-Nutzung - nur ein echt geladenes Profil,
          // nie den Notbehelf "Monteur".
          if (profResult.data) {
            try { await withTimeout(localSession.save({ user: session.user, profile: profileData }), 2000, 'localSession.save'); } catch {}
          }
        } else if (!cached && !ignore) {
          setUser(null);
          setProfile(null);
        }
      } catch (e) {
        // Offline: lokalen Cache nutzen
        console.log('[ETB Auth] Offline – nutze lokalen Cache');
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    // Notfall-Timeout: Loading wird nach 15s zwangsweise beendet, falls obiger Code hängt
    const safety = setTimeout(() => { if (!ignore) setLoading(false); }, 15000);

    initAuth().finally(() => clearTimeout(safety));

    // Auth State Listener
    //
    // WICHTIG (v57, 15.09.2026): Der Rueckruf darf NICHTS abwarten, schon gar
    // keine Datenbank-Anfrage. Supabase ruft ihn auf, waehrend es seine
    // Anmelde-Sperre haelt (z. B. wenn die App aus dem Hintergrund zurueckkommt:
    // Kamera, Anruf, Bildschirm aus; und stuendlich beim Token-Erneuern). Jede
    // Anfrage fragt aber zuerst nach der Anmeldung und stellt sich hinter genau
    // diese Sperre - die wartet auf uns, wir auf sie. Danach hing JEDE Anfrage
    // bis zum Neustart der App ("App haengt irgendwann", Testlauf 13.09.2026).
    // Deshalb: Arbeit per setTimeout aus der Sperre herausloesen.
    const { data: { subscription } } = auth.onAuthStateChange((event, session) => {
      if (ignore) return;
      setTimeout(async () => {
        if (ignore) return;
        if (session) {
          // Token nur erneuert, Profil desselben Nutzers schon da: nicht neu laden.
          // (echtesProfil wird beim Abmelden geleert - dann wird neu geladen.)
          if (event === 'TOKEN_REFRESHED' && echtesProfil.current && echtesProfil.current.id === session.user.id) {
            setUser(session.user);
            return;
          }
          try {
            const { data: prof } = await withTimeout(db.getUserProfile(session.user.id), 12000, 'getUserProfile');
            if (ignore) return;
            const bekannt = echtesProfil.current?.id === session.user.id ? echtesProfil.current : null;
            const profileData = prof || bekannt || { role: 'monteur', name: session.user.email };
            if (prof) echtesProfil.current = prof;
            setUser(session.user);
            setProfile(profileData);
            if (prof) await withTimeout(localSession.save({ user: session.user, profile: profileData }), 2000, 'localSession.save');
          } catch {
            // Offline oder zu langsam: bekanntes Profil bleibt. Beim ersten
            // Anmelden gibt es keins - dann trotzdem hinein (als Monteur, nicht
            // zwischengespeichert), sonst passierte nach dem Tippen auf
            // "Anmelden" bei schwachem Netz einfach nichts.
            if (ignore) return;
            const bekannt = echtesProfil.current?.id === session.user.id ? echtesProfil.current : null;
            setUser(session.user);
            setProfile(bekannt || { role: 'monteur', name: session.user.email });
          }
        } else if (event === 'INITIAL_SESSION') {
          // Start ohne Netz mit abgelaufenem Token: Supabase meldet "keine
          // Sitzung". Das ist KEIN Abmelden - initAuth kuemmert sich um den
          // Start (lokal gemerkter Nutzer). Vorher flog der Monteur ~30 s
          // nach einem Offline-Start raus.
        } else {
          setUser(null);
          setProfile(null);
          echtesProfil.current = null;
          try { await withTimeout(localSession.clear(), 2000, 'localSession.clear'); } catch {}
        }
      }, 0);
    });

    return () => {
      ignore = true;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email, password) => {
    setError(null);
    const { error: err } = await auth.signIn(email, password);
    if (err) {
      setError(err.message === 'Invalid login credentials'
        ? 'E-Mail oder Passwort falsch.'
        : err.message);
      return false;
    }
    return true;
  };

  const signOut = async () => {
    // Liegen noch Eintraege auf dem Geraet, gehen sie erst nach dem naechsten
    // Anmelden raus - meldet sich dann jemand anderes an, lehnt die Datenbank
    // sie ab. Deshalb vorher fragen.
    const n = wartendeAnzahl();
    if (n > 0 && !window.confirm(n + ' Eingabe(n) sind noch nicht übertragen (kein Netz).\n\n'
      + 'Bitte erst abmelden, wenn wieder Empfang da ist und alles übertragen wurde.\n\n'
      + 'Trotzdem jetzt abmelden?')) return;
    // Abmelden darf nie haengen: Antwortet der Server nicht, wird die
    // Anmeldung auf dem Geraet selbst entfernt.
    echtesProfil.current = null;
    try {
      const r = await Promise.race([
        auth.signOut(),
        new Promise((_, rej) => setTimeout(() => rej(new Error('Timeout: signOut')), 8000)),
      ]);
      // Ohne Netz meldet Supabase nur einen Fehler und behaelt die Anmeldung -
      // dann war man nach dem naechsten Token-Erneuern wieder drin.
      if (r?.error) throw r.error;
    } catch {
      try {
        Object.keys(localStorage)
          .filter((k) => k.startsWith('sb-') && k.endsWith('-auth-token'))
          .forEach((k) => localStorage.removeItem(k));
      } catch {}
    }
    try { await localSession.clear(); } catch {}
    setUser(null);
    setProfile(null);
  };

  const resetPassword = async (email) => {
    const { error: err } = await auth.resetPassword(email);
    if (err) return { success: false, message: err.message };
    return { success: true, message: `Passwort-Reset-Link wurde an ${email} gesendet.` };
  };

  const updatePassword = async (newPassword) => {
    const { error: err } = await auth.updatePassword(newPassword);
    if (err) return { success: false, message: err.message };
    return { success: true };
  };

  // Rollen-Checks
  const isAdmin = profile?.role === 'admin';
  const isBuero = profile?.role === 'buero' || isAdmin;
  const isMonteur = !!user; // Alle eingeloggten Nutzer

  return (
    <AuthContext.Provider value={{
      user, profile, loading, error,
      signIn, signOut, resetPassword, updatePassword,
      isAdmin, isBuero, isMonteur,
      role: profile?.role || 'monteur',
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
